import type { PreBookingResponse } from '~/api/prebooking'
import type {
  PrebookingDataLayerMetaValues,
  PrebookingDataLayerObject,
  PrebookingDataLayerValues,
} from '~/types/datalayer'
import { Tracking } from '@meeseeks/centaur-tracking'
import * as Sentry from '@sentry/vue'
import { storeToRefs } from 'pinia'
import { PreBookingRequestVehicleCategoryEnum } from '~/api/prebooking'
import { useOfferAdditionalOptions } from '~/composables/offer-additional-options'
import { getOfferDaysDuration } from '~/composables/offer-utils'
import { isRunningOnFree2MoveDomain } from '~/composables/tracking/tracking-scripts'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { useReferralStore } from '~/stores/referral'
import { useVehiclesStore } from '~/stores/vehicles'

const tracking = new Tracking()
const PREBOOKING_EVENT_NAME = 'prebooking_success'

export function useDataLayer() {
  function pushToDataLayer<T = any>(payload: T) {
    tracking.pushToDataLayer(payload)
  }

  function pushPendingEvents() {
    tracking.sendPendingCustomEventsToDatalayer()
  }

  function pushSuccessfulPrebookingEvent(prebookingResponse: PreBookingResponse) {
    try {
      pushToDataLayer<PrebookingDataLayerObject>({
        ...buildSuccessfulPrebookingDataLayerMetaEvent(),
        ...buildPrebookingDataLayerObject(prebookingResponse),
      })
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  // Unified prebooking event push function
  function pushPrebookingEvent<T extends keyof PrebookingEventFields>(
    eventName: T,
    fields: PrebookingEventFields[T],
  ) {
    try {
      pushToDataLayer(buildPrebookingEvent(eventName, fields))
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  // Individual push functions (using unified approach)
  function pushPrebookingCategoryEvent() {
    pushPrebookingEvent('prebooking_category', ['operator'])
  }

  function pushPrebookingAddressClickButtonEvent() {
    pushPrebookingEvent('prebooking_address_click_button', ['country', 'city', 'operator', 'duration'])
  }

  function pushPrebookingCarClickButtonEvent() {
    pushPrebookingEvent('prebooking_car_click_button', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration'])
  }

  function pushPrebookingCoverageEvent() {
    pushPrebookingEvent('prebooking_coverage', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration'])
  }

  function pushPrebookingOptionsEvent() {
    pushPrebookingEvent('prebooking_options', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance'])
  }

  function pushPrebookingPersonalEvent() {
    pushPrebookingEvent('prebooking_personal', ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance', 'extras'])
  }

  return {
    pushPendingEvents,
    pushSuccessfulPrebookingEvent,
    pushToDataLayer,
    pushPrebookingCategoryEvent,
    pushPrebookingAddressClickButtonEvent,
    pushPrebookingCarClickButtonEvent,
    pushPrebookingCoverageEvent,
    pushPrebookingOptionsEvent,
    pushPrebookingPersonalEvent,
  }
}

// Prebooking Funnel Event Data Collection Utilities
function collectCommonPrebookingData() {
  const locationStore = useLocationStore()
  const referralStore = useReferralStore()
  const vehiclesStore = useVehiclesStore()
  const extrasStore = useExtrasStore()

  // Get basic location data
  const { city } = storeToRefs(locationStore)

  // Get operator only if from meta partner (Kayak, Liligo, etc.)
  const operator = referralStore.isMetaFlow ? referralStore.leadPlatform : undefined

  // Get duration - try from offer first, fallback to calculating from selected dates
  const offerDuration = getOfferDaysDuration()
  let duration = offerDuration

  // If no offer duration available, calculate from selected dates
  if (!duration) {
    const dateTimeStore = useDateTimeStore()
    if (dateTimeStore.startDate.dateTime && dateTimeStore.endDate.dateTime) {
      const startDate = new Date(dateTimeStore.startDate.dateTime)
      const endDate = new Date(dateTimeStore.endDate.dateTime)
      const timeDiff = endDate.getTime() - startDate.getTime()
      duration = Math.ceil(timeDiff / (1000 * 3600 * 24)) // Convert to whole days
    }
  }

  // Get vehicle data if available
  const selectedOffer = useOfferStore().selectedOffer
  const vehicleCategory = vehiclesStore.getVehicleCategoryData()?.vehicleCategory

  // Map insurance from extras store (plus → plus_insurance, basic → basic_insurance)
  const { option: plusOption } = useOfferAdditionalOptions('plus_insurance')
  const insurance = extrasStore.propertyChosen(plusOption.value?.id) ? 'plus_insurance' : 'basic_insurance'

  // Get extras data
  const { option: crossBorderOption } = useOfferAdditionalOptions(`cross_border_${city.value.country}`, '.')
  const { option: comfortOption } = useOfferAdditionalOptions('cleaned', '\n', 'refueled')
  const { option: additionalDriverOption } = useOfferAdditionalOptions('additional_driver', '\n')

  return {
    country: city.value.country, // 2-letter country code
    city: city.value.id, // English city name
    operator,
    duration,
    vehicle_category: vehicleCategory ? PreBookingRequestVehicleCategoryEnum[vehicleCategory] : undefined,
    build_series: selectedOffer?.buildSeries,
    insurance,
    extras: {
      comfort_package: extrasStore.propertyChosen(comfortOption.value?.id) ? 'true' : 'false',
      cross_border: extrasStore.propertyChosen(crossBorderOption.value?.id) ? 'true' : 'false',
      additional_driver: extrasStore.propertyChosen(additionalDriverOption.value?.id) ? 'true' : 'false',
    },
  }
}

// Field configurations for each event type
interface PrebookingEventFields {
  prebooking_category: ['operator']
  prebooking_address_click_button: ['country', 'city', 'operator', 'duration']
  prebooking_car_click_button: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration']
  prebooking_coverage: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration']
  prebooking_options: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance']
  prebooking_personal: ['country', 'city', 'vehicle_category', 'build_series', 'operator', 'duration', 'insurance', 'extras']
}

// Unified event builder function
export function buildPrebookingEvent<T extends keyof PrebookingEventFields>(
  eventName: T,
  fields: PrebookingEventFields[T],
) {
  const baseEvent = {
    event: 'ga4_event' as const,
    event_name: eventName,
  }

  // For category event, only need operator (special case)
  if (eventName === 'prebooking_category') {
    const referralStore = useReferralStore()
    return {
      ...baseEvent,
      operator: referralStore.isMetaFlow ? referralStore.leadPlatform : undefined,
    }
  }

  // For all other events, collect common data and pick required fields
  const commonData = collectCommonPrebookingData()
  const eventData: any = { ...baseEvent }

  fields.forEach((field) => {
    if (field in commonData) {
      eventData[field] = commonData[field as keyof typeof commonData]
    }
  })

  return eventData
}

function buildSuccessfulPrebookingDataLayerMetaEvent(): PrebookingDataLayerMetaValues {
  return {
    event: isRunningOnFree2MoveDomain() ? 'ga4_event' : 'ce_prebooking_success',
    event_name: PREBOOKING_EVENT_NAME,
  }
}

function buildPrebookingDataLayerObject(prebookingResponse: PreBookingResponse): PrebookingDataLayerValues {
  /*
  Since tracking is loaded before the main component is mounted, we need to create store references during runtime.
  At this point, all information is available in the stores.
   */
  const extraStore = useExtrasStore()
  const { option: plusOption } = useOfferAdditionalOptions('plus_insurance')
  const { option: crossBorderOption } = useOfferAdditionalOptions(`cross_border_${useLocationStore().city.country}`, '.')
  const { option: comfortOption } = useOfferAdditionalOptions('cleaned', '\n', 'refueled')
  const { city } = storeToRefs(useLocationStore())
  const category = useVehiclesStore().getVehicleCategoryData()?.vehicleCategory

  return {
    prebooking_id: prebookingResponse.preBookingUuid,
    country: city.value.country,
    location: city.value.id,
    value: (useOfferStore().selectedOffer?.price ?? 0) + useExtrasStore().totalExtraPrice,
    currency: useOfferStore().selectedOffer?.currency ?? '',
    prebooking_vehicle_category: PreBookingRequestVehicleCategoryEnum[category!],
    prebooking_insurance: extraStore.propertyChosen(plusOption.value?.id) ? 'plus_coverage' : 'basic_coverage',
    prebooking_extras: {
      comfort_package: extraStore.propertyChosen(comfortOption.value?.id) ? 'true' : 'false',
      cross_border: extraStore.propertyChosen(crossBorderOption.value?.id) ? 'true' : 'false',
      additional_driver: prebookingResponse.additionalDriverCode ? 'true' : 'false',
    },
    prebooking_new_user: prebookingResponse.shouldRegister ? 'true' : 'false',
  }
}
